import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader } from "../Loader/Loader";
import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { useMemo } from "react";
import { Formik } from "formik";
import { ArrowRightIcon } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { InfoIcon } from "@/components/Icons/InfoIcon";
import { AlertDescription } from "@/components/ui/alert";
import { ProfileModernIcon } from "@/components/Icons/ProfileModernIcon";
import { MailIcon } from "@/components/Icons/MailIcon";
import { CubeIcon } from "@/components/Icons/CubeIcon";
import { CreditCardPlusIcon } from "@/components/Icons/CreditCardPlusIcon";
import { PassportIcon } from "@/components/Icons/PassportIcon";
import { getUserAttribute } from "@/utils/auth0";
import { AuthMachineContext } from "@/store/machine/authMachine/authMachine";
import * as Yup from "yup";
import { REQUIRED_MESSAGE, stringSchema } from "@/utils/form";
import { requestCredits } from "@/utils/credits";
import { useNotificationStore } from "@/store/notification";
import { useToast } from "@/components/ui/use-toast";
import { AnalyticsEventType } from "@/constants/analytics";
import { trackEvent } from "@/utils/analytics";

export type PurchaseCreditsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  className?: string;
};

export const PurchaseCreditsModal: React.FC<PurchaseCreditsModalProps> = ({ isOpen, onClose }) => {
  const profile = AuthMachineContext.useSelector((state) => state.context.profile);
  const initialValues = useMemo(() => {
    return {
      name: "",
      reason: "",
      amount: undefined
    } as unknown as PurchaseCreditsFormType;
  }, []);
  const displayError = useNotificationStore((state) => state.displayError);
  const { toast } = useToast();

  return (
    <Dialog open={isOpen} modal defaultOpen={isOpen}>
      <DialogContent
        className={cn(
          "custom-scrollbar max-h-[100vh] w-full gap-0 overflow-y-auto bg-gray-dark-1400 px-0 py-0 !pr-0 lg:min-w-[560px]"
        )}
        showCloseIcon={false}
      >
        <DialogHeader className="gap-2 p-5">
          <DialogTitle className="text-2xl">Request IO Credits</DialogTitle>
          <div className="text-base text-gray-dark-1000">
            Unlock faster processing, optimized payments, and streamlined efficiency with IO
            Credits.
          </div>
        </DialogHeader>

        <Formik<PurchaseCreditsFormType>
          initialValues={initialValues}
          onSubmit={async (values) => {
            try {
              const { amount, reason } = values;

              await requestCredits(values);

              trackEvent(AnalyticsEventType.REQUEST_CREDIT_SUBMITTED, {
                amount,
                purpose: reason
              });

              toast({ title: "IO Request credits received", variant: "success" });

              onClose?.();
            } catch (e) {
              if (e instanceof Error) {
                displayError({
                  title: "ERROR",
                  message: e.message
                });
              }
            }
          }}
          validationSchema={PurchaseCreditsFormSchema}
          validateOnMount={true}
        >
          {({
            handleSubmit,
            handleBlur,
            handleChange,
            values,
            touched,
            isValid,
            isSubmitting,
            errors
          }) => {
            return (
              <form onSubmit={handleSubmit} className="flex flex-col">
                <div className="flex flex-col gap-5 border-y border-gray-dark-900 p-5">
                  <div className="flex flex-col gap-2">
                    <Label>
                      Email <span className="text-blue-light-300">*</span>
                    </Label>
                    <div className="relative">
                      <MailIcon className="absolute top-[50%] ml-[8px] mt-[-9px] h-[18px] text-gray-dark-1500" />
                      <Input
                        name="email"
                        value={getUserAttribute(profile, "email")}
                        placeholder="hello@io.net"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        variant="secondary"
                        className="pl-[34px]"
                        autoComplete={false}
                        readOnly={true}
                        disabled={true}
                      />
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Label>
                      Name <span className="text-blue-light-300">*</span>
                    </Label>
                    <div className="relative">
                      <ProfileModernIcon className="absolute top-[50%] ml-[8px] mt-[-9px] h-[18px] text-gray-dark-1500" />
                      <Input
                        name="name"
                        value={values.name}
                        placeholder="Your Name"
                        invalid={touched.name && !!errors.name}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        variant="secondary"
                        className="pl-[34px]"
                        autoComplete={false}
                      />
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Label>
                      Purpose <span className="text-blue-light-300">*</span>
                    </Label>
                    <Textarea
                      name="reason"
                      value={values.reason}
                      placeholder="Why do you want to buy credits?"
                      invalid={touched.reason && !!errors.reason}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      variant="secondary"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <Label>
                      Amount <span className="text-blue-light-300">*</span>
                    </Label>
                    <Input
                      type="number"
                      name="amount"
                      value={values.amount}
                      placeholder="$0.00"
                      invalid={touched.amount && !!errors.amount}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      variant="secondary"
                      autoComplete={false}
                    />
                    <div className="flex flex-row items-center gap-1">
                      <InfoIcon className="w-[18px] min-w-[18px] text-gray-dark-1500" />
                      <AlertDescription className="text-xs font-medium text-white">
                        1 IO Credit = 1 USD{" "}
                        <span className="text-gray-dark-2200">worth of compute or inference</span>
                      </AlertDescription>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col p-5">
                  <ul className="flex flex-col gap-4">
                    <li className="flex flex-row items-center gap-2">
                      <CubeIcon className="h-[26px] text-gray-dark-2200" />
                      <span className="text-sm text-gray-dark-2200">
                        Can be used for IO Cloud and IO Intelligence
                      </span>
                    </li>
                    <li className="flex flex-row items-center gap-2">
                      <CreditCardPlusIcon className="h-[26px] text-gray-dark-2200" />
                      <span className="text-sm text-gray-dark-2200">
                        Enables fully automated workflows (esp. via API)
                      </span>
                    </li>
                    <li className="flex flex-row items-center gap-2">
                      <PassportIcon className="h-[26px] text-gray-dark-2200" />
                      <span className="text-sm text-gray-dark-2200">
                        No paywall friction during deployments
                      </span>
                    </li>
                  </ul>
                </div>

                <DialogFooter className="flex flex-row !justify-center gap-2 rounded-b-lg px-4 py-4 dark:border-gray-dark-900 dark:bg-black">
                  <div className="flex-1"></div>
                  <Button
                    onClick={onClose}
                    variant="outline"
                    size="sm"
                    data-testid="ConfirmModal_Button"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    size="sm"
                    disabled={isSubmitting || !isValid}
                    className="gap-2"
                  >
                    {isSubmitting ? (
                      <div className="mr-2">
                        <Loader className="!h-[12px] !w-[12px]" color="#fff" />
                      </div>
                    ) : null}
                    <span>Request IO Credits</span>
                    <ArrowRightIcon className="h-4 w-4" />
                  </Button>
                </DialogFooter>
              </form>
            );
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

type PurchaseCreditsFormType = {
  name: string;
  reason: string;
  amount: number;
};

const PurchaseCreditsFormSchema = Yup.object().shape({
  name: stringSchema().required(REQUIRED_MESSAGE),
  reason: stringSchema().required(REQUIRED_MESSAGE),
  amount: Yup.number().min(1, "To have a minimum of 1").required(REQUIRED_MESSAGE)
});
